/**
 * FILE: src/blueprint/utils/progressiveViewModel.js
 * GOAL:
 * - Normalize progressive payload into one clean UI shape.
 * - Keep server semantics intact as much as possible.
 * - Expose:
 *   - rail
 *   - steps
 *   - railContent
 *   - railAssistants
 *   - reportEntries
 *   - reportsById
 *   - reportSummaries
 *
 * RULES:
 * - `rail` = all rail entries in display order
 * - `railContent` = all non-assistant rail entries in display order
 * - `railAssistants` = rail entries with role=coach/assistant identity
 * - `steps` = all step-zone entries in display order
 * - `reportEntries` = all report entries that can be rendered in report steps,
 *   including payload-based reports like answer_review
 *
 * @author GoViral Web
 */

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function asObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function asString(value) {
  return value == null ? '' : String(value);
}

function normalizeText(value) {
  return asString(value).trim().toLowerCase();
}

function firstNonEmptyString(values) {
  for (const value of values) {
    const text = asString(value).trim();
    if (text) {
      return text;
    }
  }

  return '';
}

function dedupeById(items) {
  const seen = new Set();
  const out = [];

  for (const item of asArray(items)) {
    if (!item || typeof item !== 'object') {
      continue;
    }

    const id = asString(item.id).trim();

    if (!id) {
      out.push(item);
      continue;
    }

    if (seen.has(id)) {
      continue;
    }

    seen.add(id);
    out.push(item);
  }

  return out;
}

function normalizeAvatar(avatar) {
  const safeAvatar = asObject(avatar);

  return {
    src: asString(safeAvatar.src).trim(),
    url: asString(safeAvatar.url).trim(),
    path: asString(safeAvatar.path).trim(),
  };
}

function normalizeTyping(typing) {
  const safeTyping = asObject(typing);
  const speed = asObject(safeTyping.speed);

  return {
    speed: {
      min: Number.isFinite(Number(speed.min)) ? Number(speed.min) : 0,
      max: Number.isFinite(Number(speed.max)) ? Number(speed.max) : 0,
    },
    thinkChance: Number.isFinite(Number(safeTyping.thinkChance))
      ? Number(safeTyping.thinkChance)
      : 0,
    thinkExtraMin: Number.isFinite(Number(safeTyping.thinkExtraMin))
      ? Number(safeTyping.thinkExtraMin)
      : 0,
    thinkExtraMax: Number.isFinite(Number(safeTyping.thinkExtraMax))
      ? Number(safeTyping.thinkExtraMax)
      : 0,
    typoChance: Number.isFinite(Number(safeTyping.typoChance)) ? Number(safeTyping.typoChance) : 0,
  };
}

function mergeTyping(primaryTyping, fallbackTyping) {
  const primary = normalizeTyping(primaryTyping);
  const fallback = normalizeTyping(fallbackTyping);

  return {
    speed: {
      min: primary.speed.min || fallback.speed.min || 0,
      max: primary.speed.max || fallback.speed.max || 0,
    },
    thinkChance: primary.thinkChance || fallback.thinkChance || 0,
    thinkExtraMin: primary.thinkExtraMin || fallback.thinkExtraMin || 0,
    thinkExtraMax: primary.thinkExtraMax || fallback.thinkExtraMax || 0,
    typoChance: primary.typoChance || fallback.typoChance || 0,
  };
}

function normalizePersona(persona, assistantActive = {}) {
  const safePersona = asObject(persona);
  const safeAssistantActive = asObject(assistantActive);

  const personaAvatar = normalizeAvatar(safePersona.avatar);
  const activeAvatar = normalizeAvatar(safeAssistantActive.avatar);

  return {
    name: firstNonEmptyString([
      safePersona.name,
      safeAssistantActive.label,
      safeAssistantActive.title,
      safeAssistantActive.id,
    ]),
    slogan: asString(safePersona.slogan).trim(),
    introduction: asString(safePersona.introduction).trim(),
    avatar: {
      src: personaAvatar.src || activeAvatar.src || '',
      url: personaAvatar.url || activeAvatar.url || personaAvatar.src || activeAvatar.src || '',
      path: personaAvatar.path || activeAvatar.path || '',
    },
    typing: mergeTyping(safePersona.typing, safeAssistantActive.typing),
    tone: asString(safePersona.tone).trim(),
    style: asString(safePersona.style).trim(),
    energy: asString(safePersona.energy).trim(),
  };
}

function hasMeaningfulTyping(typing) {
  const safeTyping = normalizeTyping(typing);

  return Boolean(
    safeTyping.speed.min ||
      safeTyping.speed.max ||
      safeTyping.thinkChance ||
      safeTyping.thinkExtraMin ||
      safeTyping.thinkExtraMax ||
      safeTyping.typoChance
  );
}

function hasMeaningfulPersona(persona) {
  const safePersona = asObject(persona);

  return Boolean(
    asString(safePersona.name).trim() ||
      asString(safePersona.slogan).trim() ||
      asString(safePersona.introduction).trim() ||
      asString(safePersona.avatar?.src).trim() ||
      asString(safePersona.avatar?.url).trim() ||
      asString(safePersona.tone).trim() ||
      asString(safePersona.style).trim() ||
      asString(safePersona.energy).trim() ||
      hasMeaningfulTyping(safePersona.typing)
  );
}

function pickBestPersona(primaryPersona, fallbackPersona) {
  return hasMeaningfulPersona(primaryPersona) ? primaryPersona : fallbackPersona;
}

function resolveGlobalAssistantPersona(source) {
  return normalizePersona(source?.assistant?.persona, source?.assistant?.active);
}

function resolveEntryPersona(entry, fallbackPersona) {
  const raw = asObject(entry?.raw);
  const rawPersona = asObject(raw.persona);
  const entryPersona = asObject(entry?.persona);

  const merged = normalizePersona(
    hasMeaningfulPersona(entryPersona) ? entryPersona : rawPersona,
    {}
  );

  return {
    name: merged.name || fallbackPersona.name || '',
    slogan: merged.slogan || fallbackPersona.slogan || '',
    introduction: merged.introduction || fallbackPersona.introduction || '',
    avatar: {
      src: merged.avatar.src || fallbackPersona.avatar?.src || '',
      url:
        merged.avatar.url ||
        merged.avatar.src ||
        fallbackPersona.avatar?.url ||
        fallbackPersona.avatar?.src ||
        '',
      path: merged.avatar.path || fallbackPersona.avatar?.path || '',
    },
    typing: mergeTyping(merged.typing, fallbackPersona.typing),
    tone: merged.tone || fallbackPersona.tone || '',
    style: merged.style || fallbackPersona.style || '',
    energy: merged.energy || fallbackPersona.energy || '',
  };
}

function normalizePlacement(entry, raw) {
  const placementValue = entry?.placement ?? raw?.placement;

  if (placementValue && typeof placementValue === 'object' && !Array.isArray(placementValue)) {
    const placement = { ...placementValue };
    const zone = normalizeText(placement.zone || entry?.zone || raw?.zone) || 'rail';

    return {
      ...placement,
      zone,
    };
  }

  if (typeof placementValue === 'string') {
    return {
      zone: normalizeText(placementValue) || 'rail',
    };
  }

  return {
    zone: normalizeText(entry?.zone || raw?.zone) || 'rail',
  };
}

function normalizeEntry(entry, fallbackAssistantPersona) {
  if (!entry || typeof entry !== 'object') {
    return null;
  }

  const raw =
    entry?.raw && typeof entry.raw === 'object' && !Array.isArray(entry.raw) ? entry.raw : entry;

  const placement = normalizePlacement(entry, raw);
  const zone = placement.zone;

  const role = normalizeText(firstNonEmptyString([entry?.role, raw?.role]));
  const kind = normalizeText(firstNonEmptyString([entry?.kind, entry?.type, raw?.kind, raw?.type]));
  const delivery = normalizeText(
    firstNonEmptyString([
      entry?.delivery,
      entry?.meta?.delivery,
      raw?.delivery,
      raw?.meta?.delivery,
    ])
  );
  const assistantId = firstNonEmptyString([
    entry?.assistantId,
    entry?.meta?.assistantId,
    raw?.assistantId,
    raw?.meta?.assistantId,
  ]);
  const reportId = firstNonEmptyString([
    entry?.reportId,
    raw?.reportId,
    entry?.meta?.reportId,
    raw?.meta?.reportId,
    placement?.reportId,
    raw?.placement?.reportId,
  ]);
  const reportType = firstNonEmptyString([
    entry?.reportType,
    raw?.reportType,
    entry?.meta?.reportType,
    raw?.meta?.reportType,
  ]);
  const reportTitle = firstNonEmptyString([
    entry?.reportTitle,
    raw?.reportTitle,
    entry?.meta?.reportTitle,
    raw?.meta?.reportTitle,
  ]);
  const title = firstNonEmptyString([entry?.title, raw?.title, reportTitle]);

  const isAssistant = role === 'coach' || role === 'assistant' || Boolean(assistantId);
  const persona = isAssistant ? resolveEntryPersona(entry, fallbackAssistantPersona) : null;

  const payload = asObject(entry?.payload || raw?.payload);

  return {
    ...entry,
    raw,
    placement,
    zone,
    role,
    kind,
    delivery,
    assistantId,
    reportId,
    reportType,
    reportTitle,
    title,
    payload,
    persona,
  };
}

function isAssistantEntry(entry) {
  return entry?.role === 'coach' || entry?.role === 'assistant' || Boolean(entry?.assistantId);
}

function isReportEntry(entry) {
  return entry?.role === 'report';
}

function isStoredReportEntry(entry) {
  return isReportEntry(entry) && entry?.delivery === 'stored';
}

function isStepReportEntry(entry) {
  return isReportEntry(entry) && entry?.zone === 'steps';
}

function entryHasRenderableReportContent(entry) {
  const blocks = asArray(entry?.blocks);
  const payload = asObject(entry?.payload);

  return blocks.length > 0 || Object.keys(payload).length > 0;
}

function getReportRootId(entry) {
  return firstNonEmptyString([entry?.reportId, entry?.sourceId, entry?.id]);
}

function buildReportsById(entries) {
  const map = new Map();

  for (const entry of asArray(entries)) {
    if (!entry || typeof entry !== 'object') {
      continue;
    }

    const reportRootId = getReportRootId(entry);

    if (!reportRootId) {
      continue;
    }

    if (!map.has(reportRootId)) {
      map.set(reportRootId, []);
    }

    map.get(reportRootId).push(entry);
  }

  return Object.fromEntries(
    Array.from(map.entries()).map(([reportId, reportEntries]) => [
      reportId,
      dedupeById(reportEntries),
    ])
  );
}

function buildReportSummaries(reportsById) {
  return Object.entries(asObject(reportsById)).map(([reportId, reportEntries]) => {
    const safeEntries = asArray(reportEntries);
    const reportTitle = firstNonEmptyString([
      ...safeEntries.flatMap((entry) => [entry?.reportTitle, entry?.title]),
      reportId,
    ]);

    return {
      reportId,
      reportTitle,
      count: safeEntries.length,
    };
  });
}

function resolveAssistantPersonaFromEntries(entries, fallbackPersona) {
  for (const entry of asArray(entries)) {
    const persona = asObject(entry?.persona);

    if (hasMeaningfulPersona(persona)) {
      return persona;
    }
  }

  return fallbackPersona;
}

export function buildProgressiveViewModel(progressive) {
  const source = progressive && typeof progressive === 'object' ? progressive : {};
  const globalAssistantPersona = resolveGlobalAssistantPersona(source);

  const rail = dedupeById(
    asArray(source.rail)
      .map((entry) => normalizeEntry(entry, globalAssistantPersona))
      .filter(Boolean)
      .filter((entry) => entry.zone === 'rail')
  );

  const steps = dedupeById(
    asArray(source.steps)
      .map((entry) => normalizeEntry(entry, globalAssistantPersona))
      .filter(Boolean)
      .filter((entry) => entry.zone === 'steps')
  );

  const explicitReportEntries = dedupeById(
    asArray(source.reportEntries || source.report_entries)
      .map((entry) => normalizeEntry(entry, globalAssistantPersona))
      .filter(Boolean)
      .filter(isReportEntry)
      .filter(entryHasRenderableReportContent)
  );

  const railAssistants = dedupeById(rail.filter(isAssistantEntry));
  const railContent = dedupeById(rail.filter((entry) => !isAssistantEntry(entry)));

  const reportEntries = dedupeById(
    [
      ...explicitReportEntries.filter(isStoredReportEntry),
      ...steps.filter(isStepReportEntry),
    ].filter(entryHasRenderableReportContent)
  );

  const reportsById = buildReportsById(reportEntries);
  const reportSummaries = buildReportSummaries(reportsById);

  const resolvedAssistantPersona = pickBestPersona(
    globalAssistantPersona,
    resolveAssistantPersonaFromEntries(railAssistants, globalAssistantPersona)
  );

  return {
    ...source,
    assistant: {
      ...asObject(source.assistant),
      persona: resolvedAssistantPersona,
    },
    rail,
    steps,
    railContent,
    railAssistants,
    reportEntries,
    reportsById,
    reportSummaries,
    discoveredReportsCount: reportSummaries.length,
    discoveredReportEntriesCount: reportEntries.length,
  };
}

export default buildProgressiveViewModel;
